import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  ChatApiMessage,
  ChatQuickReply,
  ChatService,
  ChatUserType,
} from '../../../services/chat';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('chatBody') private chatBody?: ElementRef<HTMLElement>;

  isOpen = false;
  isTyping = false;
  isSending = false;
  draftMessage = '';
  unreadCount = 0;

  conversationId: number | null = null;
  sessionId = '';
  userType: ChatUserType = 'guest';
  userName = '';
  userPhone = '';
  userEmail = '';

  whatsappLink = 'https://wa.me/201033750675';

  quickReplies: ChatQuickReply[] = [];
  messages: ChatApiMessage[] = [];

  private isBrowser: boolean;
  private shouldScrollToBottom = false;
  private pollingSub?: Subscription;
  private audioContext?: AudioContext;
  private readonly pollingIntervalMs = 2000;
  private conversationStorageKey = 'atw_chat_conversation_id';
  private messagesStorageKey = 'atw_chat_messages';

  constructor(
    private chatService: ChatService,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.setDefaultMessages();

    if (this.isBrowser) {
      this.sessionId = this.chatService.getOrCreateGuestSessionId();
      this.hydrateLoggedInUser();

      const savedConversationId = localStorage.getItem(
        this.conversationStorageKey
      );

      if (savedConversationId) {
        const conversationId = Number(savedConversationId);

        if (Number.isFinite(conversationId) && conversationId > 0) {
          this.conversationId = conversationId;
          this.loadCachedMessages();
          this.startRealtimeMessages();
        } else {
          this.clearSavedConversation();
        }
      }
    }

    this.loadQuickReplies();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollMessagesToBottom();
    }
  }

  ngOnDestroy(): void {
    this.stopRealtimeMessages();
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.unreadCount = 0;
      this.queueScrollToBottom();
    }
  }

  closeChat(): void {
    this.isOpen = false;
  }

  getInputValue(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.draftMessage = input.value;
  }

  sendMessage(): void {
    const message = this.draftMessage.trim();

    if (!message || this.isSending) {
      return;
    }

    this.isSending = true;
    this.isTyping = !this.hasAdminReply(this.messages);
    this.queueScrollToBottom();

    this.chatService
      .sendMessage({
        conversation_id: this.conversationId || undefined,
        session_id: this.sessionId,
        user_type: this.userType,
        name: this.userName || undefined,
        phone: this.userPhone || undefined,
        email: this.userEmail || undefined,
        whatsapp_number: this.userPhone || undefined,
        message_type: 'text',
        message,
        source_page: this.getSourcePage(),
      })
      .subscribe({
        next: (res) => {
          this.conversationId = res.conversation.id;
          this.saveConversationId(res.conversation.id);
          this.setMessages(res.conversation.messages || []);
          this.draftMessage = '';
          this.isSending = false;
          this.isTyping = false;
          this.startRealtimeMessages();
          this.queueScrollToBottom();
        },
        error: () => {
          this.isSending = false;
          this.isTyping = false;
        },
      });
  }

  sendQuickMessage(messageKey: string): void {
    if (this.isSending) {
      return;
    }

    this.isSending = true;
    this.isTyping = !this.hasAdminReply(this.messages);
    this.queueScrollToBottom();

    this.chatService
      .sendMessage({
        conversation_id: this.conversationId || undefined,
        session_id: this.sessionId,
        user_type: this.userType,
        name: this.userName || undefined,
        phone: this.userPhone || undefined,
        email: this.userEmail || undefined,
        whatsapp_number: this.userPhone || undefined,
        message_type: 'quick_reply',
        message_key: messageKey,
        source_page: this.getSourcePage(),
      })
      .subscribe({
        next: (res) => {
          this.conversationId = res.conversation.id;
          this.saveConversationId(res.conversation.id);
          this.setMessages(res.conversation.messages || []);
          this.isSending = false;
          this.isTyping = false;
          this.startRealtimeMessages();
          this.queueScrollToBottom();
        },
        error: () => {
          this.isSending = false;
          this.isTyping = false;
        },
      });
  }

  openWhatsapp(): void {
    if (!this.isBrowser) {
      return;
    }

    window.open(this.whatsappLink, '_blank');
  }

  private startRealtimeMessages(): void {
    if (!this.isBrowser || !this.conversationId) {
      return;
    }

    // Prevent duplicate polling subscriptions after every sent message.
    if (this.pollingSub && !this.pollingSub.closed) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.pollingSub = timer(0, this.pollingIntervalMs)
        .pipe(
          switchMap(() =>
            this.chatService
              .getConversation(this.conversationId!, {
                session_id: this.sessionId,
                user_type: this.userType,
              })
              .pipe(
                catchError((err) => {
                  if (
                    err?.status === 401 ||
                    err?.status === 403 ||
                    err?.status === 404
                  ) {
                    this.ngZone.run(() => {
                      this.clearSavedConversation();
                      this.setDefaultMessages();
                      this.stopRealtimeMessages();
                    });
                  }

                  return EMPTY;
                })
              )
          )
        )
        .subscribe((res) => {
          const serverMessages = res.conversation?.messages || [];

          if (!serverMessages.length) {
            return;
          }

          this.ngZone.run(() => {
            this.updateMessagesFromServer(serverMessages);
          });
        });
    });
  }

  private stopRealtimeMessages(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private updateMessagesFromServer(serverMessages: ChatApiMessage[]): void {
    const oldMessages = this.messages || [];
    const filteredServerMessages =
      this.filterAcknowledgementAfterAdmin(serverMessages);

    if (this.haveSameMessages(oldMessages, filteredServerMessages)) {
      return;
    }

    const hadAdminReply = this.hasAdminReply(oldMessages);
    const hasAdminReplyNow = this.hasAdminReply(filteredServerMessages);
    const hadLoadedConversationMessages = oldMessages.some((message) => {
      return message.conversation_id === this.conversationId;
    });
    const oldMessageIds = new Set(oldMessages.map((message) => message.id));
    const newAdminMessagesCount = filteredServerMessages.filter((message) => {
      return (
        message.sender_type === 'admin' &&
        !oldMessageIds.has(message.id)
      );
    }).length;

    this.setMessages(filteredServerMessages);

    if (newAdminMessagesCount > 0 && hadLoadedConversationMessages) {
      if (!this.isOpen) {
        this.unreadCount += newAdminMessagesCount;
      }

      this.playIncomingMessageSound();
    }

    if (!hadAdminReply && hasAdminReplyNow) {
      this.isTyping = false;
    }
  }

  private playIncomingMessageSound(): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      this.audioContext =
        this.audioContext || new window.AudioContext();

      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const now = this.audioContext.currentTime;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, now);
      oscillator.frequency.setValueAtTime(660, now + 0.08);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

      oscillator.connect(gain);
      gain.connect(this.audioContext.destination);

      oscillator.start(now);
      oscillator.stop(now + 0.24);
    } catch {
      // Browsers can block audio before the user interacts with the page.
    }
  }

  private haveSameMessages(
    oldMessages: ChatApiMessage[],
    newMessages: ChatApiMessage[]
  ): boolean {
    if (oldMessages.length !== newMessages.length) {
      return false;
    }

    return oldMessages.every((oldMessage, index) => {
      const newMessage = newMessages[index];

      return (
        oldMessage.id === newMessage?.id &&
        oldMessage.conversation_id === newMessage?.conversation_id &&
        oldMessage.sender_type === newMessage?.sender_type &&
        oldMessage.message_type === newMessage?.message_type &&
        oldMessage.message_key === newMessage?.message_key &&
        oldMessage.message === newMessage?.message
      );
    });
  }

  private saveConversationId(conversationId: number): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.setItem(this.conversationStorageKey, String(conversationId));
  }

  private clearSavedConversation(): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.removeItem(this.conversationStorageKey);
    localStorage.removeItem(this.messagesStorageKey);
    this.conversationId = null;
  }

  private loadCachedMessages(): void {
    if (!this.isBrowser) {
      return;
    }

    const cachedMessages = localStorage.getItem(this.messagesStorageKey);

    if (!cachedMessages) {
      return;
    }

    try {
      const messages = JSON.parse(cachedMessages) as ChatApiMessage[];

      if (Array.isArray(messages) && messages.length) {
        this.messages = this.filterAcknowledgementAfterAdmin(messages);
        this.queueScrollToBottom();
      }
    } catch {
      localStorage.removeItem(this.messagesStorageKey);
    }
  }

  private saveMessages(messages: ChatApiMessage[]): void {
    if (!this.isBrowser || !messages.length) {
      return;
    }

    localStorage.setItem(this.messagesStorageKey, JSON.stringify(messages));
  }

  private setMessages(messages: ChatApiMessage[]): void {
    this.messages = this.filterAcknowledgementAfterAdmin(messages);
    this.saveMessages(this.messages);
    this.queueScrollToBottom();
  }

  private filterAcknowledgementAfterAdmin(messages: ChatApiMessage[]): ChatApiMessage[] {
    if (!this.hasAdminReply(messages)) {
      return messages;
    }

    return messages.filter((message) => !this.isAcknowledgementMessage(message));
  }

  private isAcknowledgementMessage(message: ChatApiMessage): boolean {
    const text = (message.message || '').trim();
    const isSystemSender =
      message.sender_type === 'bot' || message.sender_type === 'system';

    return (
      isSystemSender &&
      (message.message_key === 'atw.chat.messages.reply' ||
      text.includes('تم استلام رسالتك') ||
      text.includes('\u062a\u0645 \u0627\u0633\u062a\u0644\u0627\u0645 \u0631\u0633\u0627\u0644\u062a\u0643') ||
      text.includes('Your message has been received'))
    );
  }

  private hasAdminReply(messages: ChatApiMessage[]): boolean {
    return messages.some((message) => message.sender_type === 'admin');
  }

  private queueScrollToBottom(): void {
    if (!this.isBrowser) {
      return;
    }

    this.shouldScrollToBottom = true;
  }

  private scrollMessagesToBottom(): void {
    const container = this.chatBody?.nativeElement;

    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
    this.shouldScrollToBottom = false;
  }

  private setDefaultMessages(): void {
    this.messages = [
      {
        id: 1,
        conversation_id: 0,
        sender_type: 'bot',
        sender_user_id: null,
        message_type: 'system',
        message_key: 'atw.chat.messages.welcome',
        message: null,
        is_read: false,
        read_at: null,
        created_at: null,
        time: this.getCurrentTime(),
      },
      {
        id: 2,
        conversation_id: 0,
        sender_type: 'bot',
        sender_user_id: null,
        message_type: 'system',
        message_key: 'atw.chat.messages.help',
        message: null,
        is_read: false,
        read_at: null,
        created_at: null,
        time: this.getCurrentTime(),
      },
    ];
  }

  private hydrateLoggedInUser(): void {
    const token = localStorage.getItem('token');
    const user = this.getStoredUser();
    const userId =
      user?.id ||
      localStorage.getItem('user_id') ||
      localStorage.getItem('id');

    if (!token || !userId) {
      this.userType = 'guest';
      this.conversationStorageKey = 'atw_chat_conversation_id';
      this.messagesStorageKey = 'atw_chat_messages';
      return;
    }

    this.userType = this.resolveUserType(user);
    this.userName = this.resolveUserName(user);
    this.userPhone =
      this.cleanStoredValue(user?.phone) ||
      this.cleanStoredValue(localStorage.getItem('phone'));
    this.userEmail =
      this.cleanStoredValue(user?.email) ||
      this.cleanStoredValue(localStorage.getItem('email'));

    this.conversationStorageKey = `atw_chat_conversation_id_user_${userId}`;
    this.messagesStorageKey = `atw_chat_messages_user_${userId}`;
  }

  private getStoredUser(): any {
    const rawUser = localStorage.getItem('user');

    if (!rawUser) {
      return null;
    }

    try {
      return JSON.parse(rawUser);
    } catch {
      return null;
    }
  }

  private resolveUserType(user: any): ChatUserType {
    const storedUserType =
      this.cleanStoredValue(user?.user_type) ||
      this.cleanStoredValue(localStorage.getItem('user_type'));
    const legacyType =
      this.cleanStoredValue(user?.type) ||
      this.cleanStoredValue(localStorage.getItem('type'));

    if (
      storedUserType === 'candidate' ||
      storedUserType === 'company' ||
      storedUserType === 'admin'
    ) {
      return storedUserType;
    }

    if (legacyType === '1') {
      return 'admin';
    }

    if (legacyType === '10' || user?.company_id || localStorage.getItem('company_id')) {
      return 'company';
    }

    return 'candidate';
  }

  private resolveUserName(user: any): string {
    const fullName = [
      this.cleanStoredValue(user?.first_name) ||
        this.cleanStoredValue(localStorage.getItem('first_name')),
      this.cleanStoredValue(user?.last_name) ||
        this.cleanStoredValue(localStorage.getItem('last_name')),
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    return (
      fullName ||
      this.cleanStoredValue(user?.name) ||
      this.cleanStoredValue(user?.username) ||
      this.cleanStoredValue(localStorage.getItem('username'))
    );
  }

  private cleanStoredValue(value: any): string {
    const text = String(value || '').trim();

    if (!text || text === 'null' || text === 'undefined') {
      return '';
    }

    return text;
  }

  private loadQuickReplies(): void {
    this.chatService.getQuickReplies(this.userType).subscribe({
      next: (res) => {
        this.quickReplies = res.quick_replies?.length
          ? res.quick_replies
          : this.getFallbackQuickReplies();
      },
      error: () => {
        this.quickReplies = this.getFallbackQuickReplies();
      },
    });
  }

  private getFallbackQuickReplies(): ChatQuickReply[] {
    return [
      {
        id: 1,
        icon: '💼',
        label_key: 'atw.chat.quick.jobs',
        message_key: 'atw.chat.quickMessages.jobs',
        user_type: 'all',
        is_active: true,
        sort_order: 1,
        created_at: null,
        updated_at: null,
      },
      {
        id: 2,
        icon: '📄',
        label_key: 'atw.chat.quick.cv',
        message_key: 'atw.chat.quickMessages.cv',
        user_type: 'all',
        is_active: true,
        sort_order: 2,
        created_at: null,
        updated_at: null,
      },
      {
        id: 3,
        icon: '✈️',
        label_key: 'atw.chat.quick.travel',
        message_key: 'atw.chat.quickMessages.travel',
        user_type: 'all',
        is_active: true,
        sort_order: 3,
        created_at: null,
        updated_at: null,
      },
    ];
  }

  private getSourcePage(): string {
    if (!this.isBrowser) {
      return 'server';
    }

    return window.location.pathname.replace(/^\/+/, '') || 'home';
  }

  private getCurrentTime(): string {
    return new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
